import {
  client as defaultClient,
  createStoreTokenProvider,
  setupAuth,
} from '@up2stay/up2promo-api-client';

import { parseUpgradeRequiredError } from '@/core/authz/parse-upgrade-required-error';
import { env } from '@/core/env';
import { performFullLogout } from '@/core/hooks/use-logout';
import { getApiBaseUrl } from '@/core/lib/file-url';
import {
  applyPersistedAuthTokensIfNewer,
  AuthStatus,
  subscribeAuthStoreToCrossTabTokenSync,
  useAuthStore,
} from '@/core/store/auth.store';
import { usePreferencesStore } from '@/core/store/preferences.store';
import { showUpgradeRequired } from '@/core/store/upgrade-required.store';

import { i18n } from '../../../i18n-config';

const DEFAULT_LANGUAGE_HEADER = 'x-custom-lang';

/**
 * Get the language header name from Vite environment variables.
 */
function getLanguageHeaderName(): string {
  return env.VITE_APP_LANGUAGE_HEADER ?? DEFAULT_LANGUAGE_HEADER;
}

/**
 * Resolves the current locale for API requests.
 * Gets locale from Zustand preferences store.
 */
function resolveLocale(): string {
  // Access Zustand store directly (works outside React context)
  const locale = usePreferencesStore.getState().locale;
  return locale || i18n.defaultLocale;
}

// Guard to ensure initialization only happens once
let isInitialized = false;
let upgradeInterceptorInstalled = false;

/**
 * Initialize auth on the default API client.
 * This runs synchronously at module load time to ensure the client is always ready
 * before any API calls are made.
 *
 * This is a singleton pattern - initialization only happens once, even if this
 * module is imported multiple times.
 */
function initializeApiClient() {
  if (isInitialized) {
    return;
  }

  // Configure the baseURL from environment variables
  const baseURL = getApiBaseUrl();
  defaultClient.setConfig({
    baseURL,
  });

  const tokenProvider = createStoreTokenProvider(
    () => useAuthStore.getState(),
    {
      onAuthInvalidated: async () => {
        await performFullLogout();
      },
      onSessionDegraded: async () => {
        useAuthStore.getState().setAuthStatus(AuthStatus.OFFLINE);
      },
      resyncTokens: () => {
        applyPersistedAuthTokensIfNewer();
      },
    },
  );

  // Setup unified auth interceptors on the default client
  setupAuth({
    client: defaultClient,
    tokenProvider,
    languageHeaderName: getLanguageHeaderName(),
    resolveLocale,
  });

  subscribeAuthStoreToCrossTabTokenSync();

  isInitialized = true;
}

/**
 * Clear sticky OFFLINE banner once the API is reachable again.
 * Session degrade marks OFFLINE on transient refresh/me failures; successful
 * authenticated traffic means the connection is fine.
 */
function clearOfflineStatusIfRecovered() {
  const authState = useAuthStore.getState();
  if (authState.authStatus !== AuthStatus.OFFLINE) {
    return;
  }
  if (authState.user && authState.tokens?.accessToken) {
    authState.setAuthStatus(AuthStatus.AUTHENTICATED);
  }
}

function installUpgradeRequiredInterceptor() {
  if (upgradeInterceptorInstalled) return;
  upgradeInterceptorInstalled = true;

  defaultClient.instance.interceptors.response.use(
    (response) => {
      clearOfflineStatusIfRecovered();
      return response;
    },
    (error) => {
      const context = parseUpgradeRequiredError(error);
      if (context) {
        showUpgradeRequired(context);
      }
      return Promise.reject(error);
    },
  );
}

// Initialize immediately at module load time (synchronous)
// This ensures the client is always ready before any API calls
initializeApiClient();
installUpgradeRequiredInterceptor();

/**
 * Export the initialized client for direct use if needed.
 * Most API calls use the default client from '@up2stay/up2promo-api-client' directly,
 * which is already configured with auth via initializeApiClient().
 */
export { defaultClient as client };
